import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./Subscriptions.css";

const url = process.env.REACT_APP_BACKEND_URL;

const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + Number(days));
  return result;
};

const toDateInputValue = (date) => {
  if (!date) return "";
  return new Date(date).toISOString().slice(0, 10);
};

const createInitialForm = () => {
  const startDate = new Date();
  const endDate = addDays(startDate, 5);

  return {
    companyId: "",
    planName: "IMS Free Trial",
    price: 0,
    billingCycle: "monthly",
    startDate: toDateInputValue(startDate),
    endDate: toDateInputValue(endDate),
    status: "trial",
    maxWarehouses: 1,
    maxUsers: 5,
    features:
      "products, inventory, warehouses, purchases, sales, customers, suppliers, reports",
    notes: "",
  };
};

const STATUS_OPTIONS = [
  { value: "trial", label: "Trial" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "expired", label: "Expired" },
  { value: "cancelled", label: "Cancelled" },
];

const PLAN_OPTIONS = [
  "IMS Free Trial",
  "IMS Basic",
  "IMS Pro",
  "IMS Enterprise",
];

export default function Subscriptions() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState(createInitialForm);
  const [editingId, setEditingId] = useState(null);

  const [loading, setLoading] = useState(true);
  const [companiesLoading, setCompaniesLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [editingLoadingId, setEditingLoadingId] = useState(null);
  const [message, setMessage] = useState(null);

  const showMessage = (type, text) => {
    setMessage({ type, text });

    window.setTimeout(() => {
      setMessage(null);
    }, 5000);
  };

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${url}/api/subscriptions`);

      setSubscriptions(
        response.data?.subscriptions ||
          response.data?.data ||
          []
      );
    } catch (error) {
      showMessage(
        "error",
        error.response?.data?.message ||
          "Failed to load subscriptions"
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      setCompaniesLoading(true);

      const response = await axios.get(`${url}/api/company`);

      setCompanies(
        response.data?.data ||
          response.data?.companies ||
          []
      );
    } catch (error) {
      showMessage(
        "error",
        error.response?.data?.message ||
          "Failed to load companies"
      );
    } finally {
      setCompaniesLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptions();
    fetchCompanies();
  }, []);

  const sortedCompanies = useMemo(() => {
    return [...companies].sort((a, b) =>
      String(a.company_name || "").localeCompare(
        String(b.company_name || "")
      )
    );
  }, [companies]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    let updates = {
      [name]: value,
    };

    if (name === "status" && value === "trial") {
      const startDate = new Date();
      const endDate = addDays(startDate, 5);

      updates = {
        ...updates,
        planName:
          form.planName || "IMS Free Trial",
        price: 0,
        startDate: toDateInputValue(startDate),
        endDate: toDateInputValue(endDate),
      };
    }

    if (
      name === "planName" &&
      value === "IMS Free Trial"
    ) {
      const startDate = new Date();
      const endDate = addDays(startDate, 5);

      updates = {
        ...updates,
        status: "trial",
        price: 0,
        startDate: toDateInputValue(startDate),
        endDate: toDateInputValue(endDate),
      };
    }

    setForm((previous) => ({
      ...previous,
      ...updates,
    }));
  };

  const resetForm = () => {
    setForm(createInitialForm());
    setEditingId(null);
    setEditingLoadingId(null);
  };

  const validateForm = () => {
    if (!editingId && !form.companyId) {
      return "Please select a company";
    }

    if (!form.planName.trim()) {
      return "Plan name is required";
    }

    if (!form.startDate || !form.endDate) {
      return "Start date and end date are required";
    }

    if (
      new Date(form.endDate) <=
      new Date(form.startDate)
    ) {
      return "End date must be later than start date";
    }

    if (Number(form.maxWarehouses) < 1) {
      return "Maximum warehouses must be at least 1";
    }

    if (Number(form.maxUsers) < 1) {
      return "Maximum users must be at least 1";
    }

    return null;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationError = validateForm();

    if (validationError) {
      showMessage("error", validationError);
      return;
    }

    try {
      setSaving(true);

      const payload = {
        planName: form.planName.trim(),
        price:
          form.status === "trial"
            ? 0
            : Number(form.price || 0),
        billingCycle: form.billingCycle,
        startDate: form.startDate,
        endDate: form.endDate,
        status: form.status,
        maxWarehouses: Number(
          form.maxWarehouses || 1
        ),
        maxUsers: Number(form.maxUsers || 5),
        features: form.features
          ? form.features
              .split(",")
              .map((item) => item.trim())
              .filter(Boolean)
          : [],
        notes: form.notes.trim(),
      };

      if (editingId) {
        await axios.put(
          `${url}/api/subscriptions/${editingId}`,
          payload
        );

        showMessage(
          "success",
          "Subscription updated successfully"
        );
      } else {
        await axios.post(`${url}/api/subscriptions`, {
          ...payload,
          companyId: form.companyId,
        });

        showMessage(
          "success",
          "Subscription created successfully"
        );
      }

      resetForm();
      await fetchSubscriptions();
    } catch (error) {
      showMessage(
        "error",
        error.response?.data?.message ||
          "Failed to save subscription"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (subscription) => {
    setEditingLoadingId(subscription._id);

    window.setTimeout(() => {
      setEditingId(subscription._id);

      setForm({
        companyId:
          subscription.company?._id ||
          subscription.company ||
          "",
        planName:
          subscription.planName || "",
        price:
          subscription.price ?? "",
        billingCycle:
          subscription.billingCycle || "monthly",
        startDate: subscription.startDate
          ? toDateInputValue(
              subscription.startDate
            )
          : "",
        endDate: subscription.endDate
          ? toDateInputValue(subscription.endDate)
          : "",
        status:
          subscription.status || "trial",
        maxWarehouses:
          subscription.maxWarehouses || 1,
        maxUsers: subscription.maxUsers || 5,
        features: Array.isArray(
          subscription.features
        )
          ? subscription.features.join(", ")
          : "",
        notes: subscription.notes || "",
      });

      setEditingLoadingId(null);

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }, 100);
  };

  const handleCancel = async (id) => {
    const confirmed = window.confirm(
      "Cancel this subscription?"
    );

    if (!confirmed) return;

    try {
      setDeletingId(id);

      await axios.delete(`${url}/api/subscriptions/${id}`);

      showMessage(
        "success",
        "Subscription cancelled successfully"
      );

      await fetchSubscriptions();

      if (editingId === id) {
        resetForm();
      }
    } catch (error) {
      showMessage(
        "error",
        error.response?.data?.message ||
          "Failed to cancel subscription"
      );
    } finally {
      setDeletingId(null);
    }
  };

  const getCompanyName = (subscription) => {
    const company = subscription.company;

    if (!company) return "-";

    if (typeof company === "string") {
      return company;
    }

    return (
      company.company_name ||
      company.company_code ||
      company.company_id ||
      "-"
    );
  };

  const getCompanyCode = (subscription) => {
    const company = subscription.company;

    if (!company || typeof company === "string") {
      return "";
    }

    return (
      company.company_code ||
      company.company_id ||
      ""
    );
  };

  const formatDate = (date) => {
    if (!date) return "-";

    return new Date(date).toLocaleDateString();
  };

  const getRemainingDays = (
    endDate,
    status
  ) => {
    if (status === "inactive") {
      return "Inactive";
    }

    if (status === "expired") {
      return "Expired";
    }

    if (status === "cancelled") {
      return "Cancelled";
    }

    if (!endDate) {
      return "-";
    }

    const today = new Date();
    const end = new Date(endDate);

    today.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    const difference = Math.ceil(
      (end.getTime() - today.getTime()) /
        (1000 * 60 * 60 * 24)
    );

    if (difference <= 0) {
      return "Expired";
    }

    return `${difference} Day${
      difference === 1 ? "" : "s"
    } Left`;
  };

  const getStatusClass = (status) => {
    return `subscription-status ${status || ""}`;
  };

  return (
    <div className="page ims-subscriptions-page">
      <div className="page-header">
        <div>
          <h1>IMS Subscriptions</h1>
          <p>
            Manage company subscription plans,
            warehouse limits, user limits, and
            subscription status.
          </p>
        </div>

        <button
          type="button"
          onClick={() => {
            fetchSubscriptions();
            fetchCompanies();
          }}
          disabled={loading || companiesLoading}
        >
          {loading || companiesLoading
            ? "Refreshing..."
            : "Refresh"}
        </button>
      </div>

      {message && (
        <div
          className={`subscription-message ${message.type}`}
        >
          {message.text}
        </div>
      )}

      <div className="panel">
        <h3>
          {editingId
            ? "Update Subscription"
            : "Create Subscription"}
        </h3>

        <form
          onSubmit={handleSubmit}
          className="form-grid"
        >
          <div>
            <label>Company</label>

            <select
              name="companyId"
              value={form.companyId}
              onChange={handleChange}
              required={!editingId}
              disabled={
                Boolean(editingId) ||
                companiesLoading
              }
            >
              <option value="">
                {companiesLoading
                  ? "Loading companies..."
                  : "Select Company"}
              </option>

              {sortedCompanies.map((company) => (
                <option
                  key={company._id}
                  value={company._id}
                >
                  {company.company_name ||
                    company.company_code ||
                    company.company_id}
                  {company.company_code
                    ? ` (${company.company_code})`
                    : ""}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label>Plan Name</label>

            <select
              name="planName"
              value={form.planName}
              onChange={handleChange}
              required
            >
              <option value="">
                Select Plan
              </option>

              {PLAN_OPTIONS.map((plan) => (
                <option key={plan} value={plan}>
                  {plan}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label>Price</label>

            <input
              type="number"
              name="price"
              value={form.price}
              onChange={handleChange}
              min="0"
              step="0.01"
              disabled={form.status === "trial"}
            />
          </div>

          <div>
            <label>Billing Cycle</label>

            <select
              name="billingCycle"
              value={form.billingCycle}
              onChange={handleChange}
            >
              <option value="monthly">
                Monthly
              </option>
              <option value="yearly">
                Yearly
              </option>
            </select>
          </div>

          <div>
            <label>Start Date</label>

            <input
              type="date"
              name="startDate"
              value={form.startDate}
              onChange={handleChange}
              required
            />
          </div>

          <div>
            <label>End Date</label>

            <input
              type="date"
              name="endDate"
              value={form.endDate}
              onChange={handleChange}
              required
            />
          </div>

          <div>
            <label>Status</label>

            <select
              name="status"
              value={form.status}
              onChange={handleChange}
            >
              {STATUS_OPTIONS.map((status) => (
                <option
                  key={status.value}
                  value={status.value}
                >
                  {status.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label>Max Warehouses</label>

            <input
              type="number"
              name="maxWarehouses"
              value={form.maxWarehouses}
              onChange={handleChange}
              min="1"
            />
          </div>

          <div>
            <label>Max Users</label>

            <input
              type="number"
              name="maxUsers"
              value={form.maxUsers}
              onChange={handleChange}
              min="1"
            />
          </div>

          <div>
            <label>Features</label>

            <input
              name="features"
              value={form.features}
              onChange={handleChange}
              placeholder="products, inventory, reports"
            />
          </div>

          <div>
            <label>Notes</label>

            <input
              name="notes"
              value={form.notes}
              onChange={handleChange}
              placeholder="Optional notes"
            />
          </div>

          <div className="form-actions">
            <button
              type="submit"
              disabled={saving}
            >
              {saving
                ? editingId
                  ? "Updating..."
                  : "Creating..."
                : editingId
                ? "Update Subscription"
                : "Create Subscription"}
            </button>

            {editingId && (
              <button
                type="button"
                onClick={resetForm}
                disabled={saving}
              >
                Cancel Edit
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="panel">
        <h3>Subscription List</h3>

        {loading ? (
          <p>Loading subscriptions...</p>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>No</th>
                  <th>Subscription No</th>
                  <th>Company</th>
                  <th>Plan</th>
                  <th>Price</th>
                  <th>Cycle</th>
                  <th>Status</th>
                  <th>Remaining</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Warehouses</th>
                  <th>Users</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {subscriptions.map(
                  (subscription, index) => (
                    <tr key={subscription._id}>
                      <td>{index + 1}</td>

                      <td>
                        {subscription.subscriptionNo ||
                          "-"}
                      </td>

                      <td>
                        <strong>
                          {getCompanyName(
                            subscription
                          )}
                        </strong>

                        {getCompanyCode(
                          subscription
                        ) && (
                          <div className="company-code">
                            {getCompanyCode(
                              subscription
                            )}
                          </div>
                        )}
                      </td>

                      <td>
                        {subscription.planName || "-"}
                      </td>

                      <td>
                        $
                        {Number(
                          subscription.price || 0
                        ).toFixed(2)}
                      </td>

                      <td>
                        {subscription.billingCycle ||
                          "-"}
                      </td>

                      <td>
                        <span
                          className={getStatusClass(
                            subscription.status
                          )}
                        >
                          {subscription.status || "-"}
                        </span>
                      </td>

                      <td>
                        {getRemainingDays(
                          subscription.endDate,
                          subscription.status
                        )}
                      </td>

                      <td>
                        {formatDate(
                          subscription.startDate
                        )}
                      </td>

                      <td>
                        {formatDate(
                          subscription.endDate
                        )}
                      </td>

                      <td>
                        {subscription.maxWarehouses ||
                          1}
                      </td>

                      <td>
                        {subscription.maxUsers || 1}
                      </td>

                      <td>
                        <div className="table-actions">
                          <button
                            type="button"
                            className="action-btn edit"
                            onClick={() =>
                              handleEdit(
                                subscription
                              )
                            }
                            disabled={
                              editingLoadingId ===
                              subscription._id
                            }
                          >
                            {editingLoadingId ===
                            subscription._id
                              ? "Loading..."
                              : "Edit"}
                          </button>

                          {subscription.status !==
                            "cancelled" && (
                            <button
                              type="button"
                              className="action-btn cancel"
                              onClick={() =>
                                handleCancel(
                                  subscription._id
                                )
                              }
                              disabled={
                                deletingId ===
                                subscription._id
                              }
                            >
                              {deletingId ===
                              subscription._id
                                ? "Cancelling..."
                                : "Cancel"}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                )}

                {subscriptions.length === 0 && (
                  <tr>
                    <td
                      colSpan="13"
                      className="empty"
                    >
                      No subscriptions found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <style>{`
        .ims-subscriptions-page .subscription-message {
          margin-bottom: 16px;
          padding: 12px 16px;
          border-radius: 8px;
          font-weight: 600;
        }

        .ims-subscriptions-page .subscription-message.success {
          background: #e8f7ee;
          color: #18753c;
          border-left: 4px solid #28a745;
        }

        .ims-subscriptions-page .subscription-message.error {
          background: #fff0f0;
          color: #b42318;
          border-left: 4px solid #dc3545;
        }

        .ims-subscriptions-page .subscription-status {
          display: inline-block;
          padding: 5px 10px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 700;
          text-transform: capitalize;
          white-space: nowrap;
        }

        .ims-subscriptions-page .subscription-status.trial {
          color: #7a4d00;
          background: #fff3cd;
        }

        .ims-subscriptions-page .subscription-status.active {
          color: #146c43;
          background: #d1e7dd;
        }

        .ims-subscriptions-page .subscription-status.inactive {
          color: #495057;
          background: #e9ecef;
        }

        .ims-subscriptions-page .subscription-status.expired {
          color: #842029;
          background: #f8d7da;
        }

        .ims-subscriptions-page .subscription-status.cancelled {
          color: #842029;
          background: #f8d7da;
        }

        .ims-subscriptions-page .company-code {
          margin-top: 3px;
          color: #6c757d;
          font-size: 12px;
        }

        .ims-subscriptions-page .table-actions {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .ims-subscriptions-page .action-btn {
          border: none;
          border-radius: 6px;
          padding: 7px 11px;
          cursor: pointer;
        }

        .ims-subscriptions-page .action-btn.edit {
          color: #ffffff;
          background: #170f48;
        }

        .ims-subscriptions-page .action-btn.cancel {
          color: #ffffff;
          background: #dc3545;
        }

        .ims-subscriptions-page .action-btn:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }

        .ims-subscriptions-page .empty {
          padding: 24px;
          text-align: center;
          color: #6c757d;
        }

        @media (max-width: 768px) {
          .ims-subscriptions-page .page-header {
            align-items: flex-start;
            flex-direction: column;
            gap: 12px;
          }
        }
      `}</style>
    </div>
  );
}